import { Box, Button, Divider, Grid, Typography } from '@mui/material'
import { memo, useCallback, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { downloadCandidateResume } from '../../axios/api/candidate-details'
import CustomChip from '../../components/chip'
import Icon from '../../components/icon'
import {
  copyToClipboard,
  downloadPDFBlob,
  formatDateTime,
  showErrorToast
} from '../../utils/utils'

const CopyClickboardIcon = memo(({ value }) => {
  return (
    <Box onClick={() => copyToClipboard(value)} className="copy-icon">
      <Icon icon={'mdi:clipboard-multiple-outline'} title="Copy" />
    </Box>
  )
})

const DisplayCandidateData = memo(({ header, data, isCopy = false }) => {
  return (
    <Box className="candidate-info-wrapper">
      <Box className="candidate-info">
        <Typography className="label">{header}</Typography>
        <Typography className="value">{data || '-'}</Typography>
      </Box>

      {data && isCopy && <CopyClickboardIcon value={data} />}
    </Box>
  )
})

const CandidateEvaluationDetails = memo(
  ({ data, setLoading, summaryPageLink = '', isLargeScreen = false }) => {
    const resumeExists = useMemo(() => {
      return data?.resume || null
    }, [data?.resume])

    const handleDownloadResume = useCallback(async () => {
      const payload = {
        contestId: data?.resumeContestId,
        candidateId: data?.candidateId,
        fieldId: data?.fieldId || null
      }
      setLoading(true)
      const response = await downloadCandidateResume(payload)
      setLoading(false)

      if (response?.isError) {
        showErrorToast(response)
      } else {
        downloadPDFBlob(response, 'Resume.pdf')
      }
    }, [data?.fieldId, data?.candidateId, data?.resumeContestId, setLoading])

    return (
      <Box className="candidate-evaluation-details">
        <Box className="candidate-evaluation-details-header">
          <Typography sx={{ fontSize: '24px', fontWeight: '700' }}>
            Candidate Information
          </Typography>

          {isLargeScreen && (
            <Button
              variant="contained"
              startIcon={<Icon icon={'solar:user-id-outline'} />}
              component={Link}
              to={summaryPageLink}
            >
              Candidate Summary
            </Button>
          )}
        </Box>
        <Box className="candidate-evaluation-details-body">
          <Box className="candidate-profile-pic">
            {data?.profilePicUri ? (
              <img src={data?.profilePicUri} alt="Candidate Profile" />
            ) : (
              <Icon
                icon="radix-icons:avatar"
                width="120px"
                height="120px"
                title="No profile picture found"
              />
            )}
          </Box>
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <DisplayCandidateData header="Name" data={data?.candidateName} />
            </Grid>
            <Grid item xs={12} md={6}>
              <DisplayCandidateData header="Email" data={data?.email} isCopy />
            </Grid>
            <Grid item xs={12} md={6}>
              <DisplayCandidateData
                header="Phone Number"
                data={data?.phoneNumber}
                isCopy
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography
                sx={{
                  fontSize: '13px',
                  fontWeight: '600',
                  color: 'var(--grey-500)'
                }}
              >
                Resume
              </Typography>
              {resumeExists ? (
                <Box>
                  <Button
                    size="small"
                    startIcon={
                      <Icon
                        icon={'mdi:download'}
                        style={{ fontSize: '15px' }}
                      />
                    }
                    onClick={handleDownloadResume}
                  >
                    Download Resume
                  </Button>
                </Box>
              ) : (
                <Typography fontSize={'15px'} fontWeight={'600'}>
                  -
                </Typography>
              )}
            </Grid>
            {!isLargeScreen && (
              <Grid item xs={12}>
                <Button
                  size={'small'}
                  variant="contained"
                  startIcon={<Icon icon={'solar:user-id-outline'} />}
                  component={Link}
                  to={summaryPageLink}
                >
                  Candidate Summary
                </Button>
              </Grid>
            )}
          </Grid>
        </Box>
        <Divider sx={{ width: '100%' }} />
        <Box className="candidate-assessment-details">
          <Typography className="title">Assessment Information</Typography>
        </Box>
        <Grid
          sx={{ flex: 1, padding: '15px 24px 24px 24px' }}
          container
          spacing={6}
        >
          <Grid item xs={12} md={6} className="contest-url-container">
            <DisplayCandidateData
              header="Assessment Name"
              data={data?.contestName}
            />
          </Grid>
          <Grid item xs={12} md={6} className="contest-url-container">
            <DisplayCandidateData
              header="Assessment Date"
              data={formatDateTime(data?.startDate, true, false)}
            />
          </Grid>

          <Grid item xs={12} md={6} className="contest-url-container">
            <DisplayCandidateData header="Job Title" data={data?.jobTitle} />
          </Grid>

          <Grid item xs={12} md={6} className="contest-url-container">
            <Box>
              <Typography
                sx={{
                  fontSize: '13px',
                  fontWeight: '600',
                  color: 'var(--grey-500)'
                }}
              >
                Job Skills
              </Typography>
              {data?.technologies?.length > 0 ? (
                <Box
                  sx={{
                    gap: '10px',
                    display: 'flex',
                    overflow: 'hidden',
                    flexWrap: 'wrap',
                    paddingTop: '5px'
                  }}
                >
                  {data?.technologies?.map((value, index) => (
                    <CustomChip
                      size="small"
                      key={`${value}-${index}`}
                      label={value}
                    />
                  ))}
                </Box>
              ) : (
                <Typography fontSize={'15px'} fontWeight={'500'}>
                  -
                </Typography>
              )}
            </Box>
          </Grid>
        </Grid>
      </Box>
    )
  }
)

export default CandidateEvaluationDetails
